/* eslint-disable react-hooks/exhaustive-deps */
import clsx from "clsx";
import { inject, observer } from "mobx-react";
import Image from "next/image";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Store } from "stores/store";
import ReactMarkdown from "react-markdown";
import { observe } from "mobx";
import { getAltTextFromGeneralImage, getImageUrlFromGeneralImage } from "utils/imageHelpers";
import { UiState } from "../enums/UiState";
import { ComponentChatMessageButton, ComponentChatMessageText } from "types/chat";
import { motion, AnimatePresence } from "framer-motion";
import { ComponentGeneralImage, Maybe, UploadFileEntityResponse } from "types/strapi";
import ExportedImage from "next-image-export-optimizer";
import { cn } from "@/lib/utils";
import ChatButton from "../web/chat/ChatButton";
import { ChatComponent } from "@/components/chat/hooks/gpt/utils";
import { TrackingContext } from "../shared/tracking/Tracking";

interface ChatMessageButtonProps {
    characterImageSrc: string;
    characterImageAlt: string;
    title: string;
    text: string;
    buttonText: string;
    buttonTextTwo: string;
    buttonTextThree: string | undefined;
    buttonId: string;
    buttonIdTwo: string;
    buttonIdThree: string;
    store?: Store;
    isMainChatMessage: boolean;
    buttons: ComponentChatMessageButton[];
    timeToAppear?: Date;
    buttonEmoji: Maybe<UploadFileEntityResponse>;
    buttonTwoEmoji: Maybe<UploadFileEntityResponse>;
    buttonThreeEmoji: Maybe<UploadFileEntityResponse>;
    buttonEmojiAlt: Maybe<string>;
    buttonTwoEmojiAlt: Maybe<string>;
    buttonThreeEmojiAlt: Maybe<string>;
    isOver50?: boolean;
    onClick?: (message: string, isGptQuestion: boolean, chatComponents: ChatComponent[]) => Promise<void>;
    isByod?: boolean;
}

const ChatMessageButton: React.FunctionComponent<ChatMessageButtonProps> = (props) => {
    const { onClick } = props;

    if (!props.store) {
        throw Error("Store was not setup correctly");
    }
    const isOnline: boolean = process.env.NEXT_PUBLIC_IS_ONLINE === "true";
    const isEvent = props.isByod ? false : process.env.NEXT_PUBLIC_IS_EVENT === "true";

    const {
        chatBranches,
        actualChatThread,
        updateActualChatThread,
        chatMessageEnlarged,
        updateChatBackground,
        updateChatEffect,
        updateChatTimer,
        chatAiBranches,
        chatBranchesOver50,
        chatEventBranches,
    } = props.store;

    const buttonId = parseInt(props.buttonId);
    const buttonIdTwo = parseInt(props.buttonIdTwo);
    const buttonIdThree = parseInt(props.buttonIdThree);

    const { updateButtonById, buttonsPressed, updateRenderContent } = props.store;

    const [height, setHeight] = useState<number>(0);
    const [animationFinished, setAnimationFinished] = useState<boolean>(false);

    const [buttonsPressedObserved, setButtonsPressedObserved] = useState<boolean[]>(props.store.buttonsPressed);
    const [buttonPressed, setButtonPressed] = useState<boolean>(buttonsPressed[buttonId]);

    const chatMessageRef = useRef<HTMLDivElement>(null);

    const [selectionMade, setSelectionMade] = useState<boolean>(false);

    useEffect(() => {
        if (chatMessageRef.current) {
            setHeight(chatMessageRef.current.clientHeight);
            setAnimationFinished(true);
        }
    }, []);

    useEffect(() => {
        const disposer = observe(buttonsPressed, (change) => {
            const achievementsNew = [...change.object];
            setButtonsPressedObserved(achievementsNew);
        });

        return () => disposer();
    }, []);

    useEffect(() => {
        if (buttonsPressedObserved[buttonId] !== buttonPressed) {
            setButtonPressed(buttonsPressedObserved[buttonId]);
        }
    }, [buttonsPressedObserved]);

    const addBranchToThread = useCallback(
        (button: ComponentChatMessageButton) => {
            updateChatEffect("");

            const nextBranch = chatBranches.find(
                (el) => button?.chat_branch !== null && el?.id === button?.chat_branch.data?.id,
            );

            const now = new Date();
            let staggeredDelay = now.getTime() + 300;

            // create a user response from the selected button
            const textFromButton: ComponentChatMessageText = {
                __typename: "ComponentChatMessageText",
                name: button?.name,
                text: button?.text ? button?.text : button.label,
                parameters: {
                    delay: 0.3,
                    duration: 0,
                    switchBackground: null,
                    triggerEffect: null,
                },
                timeToAppear: new Date(staggeredDelay),
                isUser: true,
                showSendersName: true,
                emoji: button.emojiAlt !== undefined && button.emojiAlt !== null ? button.emojiAlt : "",
            };

            console.log("textFromButton ", textFromButton)

            // equip template messages with additional fields like the time when to appear
            const nextBranchMessages =
                nextBranch?.attributes?.chatMessages.map((e, i) => {
                    staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                    staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                    return {
                        ...e, // clone the template

                        timeToAppear: new Date(staggeredDelay), // staggered animation, first in line is the button message above
                        isUser: false,
                        showSendersName: i === 0,
                    };
                }) ?? [];

            const nextBranchAi = chatAiBranches.find(
                (el) => button?.chat_branch_ai !== null && el?.id === button?.chat_branch_ai.data?.id,
            );

            if (nextBranchAi?.attributes?.name) screenView(nextBranchAi?.attributes?.name)

            const nextBranchAiMessages =
                nextBranchAi?.attributes?.chatMessages.map((e, i) => {
                    staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                    staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                    return {
                        ...e, // clone the template

                        timeToAppear: new Date(staggeredDelay), // staggered animation, first in line is the button message above
                        isUser: false,
                        showSendersName: i === 0,
                    };
                }) ?? [];

            const nextBranchOver50 = chatBranchesOver50.find(
                (el) => button?.chat_branch_ai !== null && el?.id === button?.chat_branch_ai.data?.id,
            );

            const nextBranchOver50Messages =
                nextBranchOver50?.attributes?.chatMessages.map((e, i) => {
                    staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                    staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                    return {
                        ...e, // clone the template

                        timeToAppear: new Date(staggeredDelay), // staggered animation, first in line is the button message above
                        isUser: false,
                        showSendersName: i === 0,
                    };
                }) ?? [];

            const nextBranchEvent = chatEventBranches.find(
                (el) => button?.chat_event !== null && el?.id === button?.chat_event.data?.id,
            );

            const nextBranchEventMessages =
                nextBranchEvent?.attributes?.chatMessages.map((e, i) => {
                    staggeredDelay += (e?.parameters?.delay ?? 0) * 1000;
                    staggeredDelay += (e?.parameters?.duration ?? 0) * 1000;

                    return {
                        ...e, // clone the template

                        timeToAppear: new Date(staggeredDelay), // staggered animation, first in line is the button message above
                        isUser: false,
                        showSendersName: i === 0,
                    };
                }) ?? [];

            const buttonText: string = (button?.text ? button?.text?.data?.attributes?.translation : button?.label?.data?.attributes?.translation) || "Fehler";

            // DO NOT TOUCH
            if (button.isGptQuestion) {
                actualChatThread.pop();
                // updateActualChatThread(
                //     isOnline
                //         ? isEvent
                //             ? actualChatThread.concat([textFromButton]).concat(nextBranchEventMessages)
                //             : props.isOver50
                //                 ? actualChatThread.concat([textFromButton]).concat(nextBranchOver50Messages)
                //                 : actualChatThread.concat([textFromButton]).concat(nextBranchAiMessages)
                //         : actualChatThread.concat([textFromButton]).concat(nextBranchMessages)
                // );
                if (props.isByod) {
                    updateActualChatThread(
                        actualChatThread.concat([textFromButton]).concat(nextBranchAiMessages)
                    );
                } else {
                    updateActualChatThread(
                        actualChatThread.concat([textFromButton]).concat(nextBranchEventMessages)
                    );
                }

                if (onClick) {
                    if (props.isByod) {
                        onClick(buttonText, true, nextBranchAiMessages);
                    } else {
                        onClick(buttonText, false, nextBranchEventMessages);
                    }

                    // if (isEvent) {
                    //     onClick(buttonText, false, nextBranchEventMessages);
                    // } else {
                    //     onClick(buttonText, false, nextBranchAiMessages);
                    // }
                }
            } else {
                if (onClick) {
                    // if (isEvent) {
                    //     onClick(buttonText, false, nextBranchEventMessages);
                    // } else {
                    //     onClick(buttonText, false, nextBranchAiMessages);
                    // }
                    if (props.isByod) {
                        onClick(buttonText, false, nextBranchAiMessages);
                    } else {
                        onClick(buttonText, false, nextBranchEventMessages);
                    }
                }
                actualChatThread.pop();
                // updateActualChatThread(
                //     isOnline
                //         ? isEvent
                //             ? actualChatThread.concat([textFromButton]).concat(nextBranchEventMessages)
                //             : props.isOver50
                //                 ? actualChatThread.concat([textFromButton]).concat(nextBranchOver50Messages)
                //                 : actualChatThread.concat([textFromButton]).concat(nextBranchAiMessages)
                //         : actualChatThread.concat([textFromButton]).concat(nextBranchMessages)
                // );
                if (props.isByod) {
                    updateActualChatThread(
                        actualChatThread.concat([textFromButton]).concat(nextBranchAiMessages)
                    );
                } else {
                    updateActualChatThread(
                        actualChatThread.concat([textFromButton]).concat(nextBranchEventMessages)
                    );
                }

            }

            updateChatBackground('');
            updateChatEffect('');
        },

        [chatBranches, actualChatThread, chatAiBranches, chatBranchesOver50, chatEventBranches],
    );

    const delay = props.timeToAppear ? Math.max(props.timeToAppear.getTime() - new Date().getTime(), 0) / 1000 : 0;

    const variantsContainer = {
        hidden: { opacity: 0, pointerEvents: "none" as React.CSSProperties["pointerEvents"] },
        shown: {
            opacity: 1,
            pointerEvents: "all" as React.CSSProperties["pointerEvents"],
            transition: { duration: 1, delay: delay },
        },
        enlarged: {
            opacity: 0,
            pointerEvents: "none" as React.CSSProperties["pointerEvents"],
            transition: { duration: 1, delay: 0 },
        },
    };

    const { screenView } = useContext(TrackingContext);

    return (
        <AnimatePresence>
            <motion.div
                className={cn("ChatMessageButton |", isOnline ? "" : "fixed left-0 top-[2780px] w-screen")}
                variants={variantsContainer}
                initial="hidden"
                animate={chatMessageEnlarged ? "enlarged" : "shown"}
                exit="hidden"
            >
                <div className="hidden relative opacity-0 pointer-events-none">
                    {/* <div */}
                    {/*     className={clsx("invisible absolute flex flex-row gap-8 bg-elevation-1 pb-4 overflow-y-hidden")} */}
                    {/*     ref={chatMessageRef} */}
                    {/* > */}
                    {/*     <div className={clsx("text-white", isOnline ? "" : "my-8 mr-8")}> */}
                    {/*         <div */}
                    {/*             className={clsx( */}
                    {/*                 "text-bold-dynamic-petrol w-full font-black uppercase tracking-[1px]", */}
                    {/*                 isOnline ? "text-[8px]" : "text-[48px]", */}
                    {/*             )} */}
                    {/*         > */}
                    {/*             {props.title} */}
                    {/*         </div> */}
                    {/*         <div */}
                    {/*             className={clsx("mt-2 w-full leading-[22px]", isOnline ? "text-[12px]" : "text-[52px]")} */}
                    {/*         > */}
                    {/*             <ReactMarkdown>{props.text}</ReactMarkdown> */}
                    {/*         </div> */}
                    {/*         <div className="mt-[16px] w-full"> */}
                    {/*             <button */}
                    {/*                 disabled={buttonPressed} */}
                    {/*                 onClick={() => { */}
                    {/*                     //console.debug('updateButtonById', buttonId) */}
                    {/*                     //updateButtonById(buttonId, true); */}
                    {/*                     updateChatTimer(false); */}
                    {/*                     addBranchToThread(props.buttons[0]); */}
                    {/*                 }} */}
                    {/*                 className="flex flex-col items-end w-full" */}
                    {/*             > */}
                    {/*                 <div */}
                    {/*                     className={clsx( */}
                    {/*                         "w-full font-black", */}
                    {/*                         "flex flex-row items-center justify-center", */}
                    {/*                         "font-frutigerRoman", */}
                    {/*                         isOnline */}
                    {/*                             ? "selectionBubbleOnline px-[16px] py-[12px] text-[12px]" */}
                    {/*                             : "selectionBubble px-16 py-12 text-[52px]", */}
                    {/*                     )} */}
                    {/*                 > */}
                    {/*                     {props.buttonText} */}
                    {/*                     {props.buttonEmoji && props.buttonEmojiAlt && ( */}
                    {/*                         <div className="relative w-[90px] min-w-[90px] max-w-[90px] h-[90px] min-h-[90px] max-h-[90px]"> */}
                    {/*                             <ExportedImage */}
                    {/*                                 src={ */}
                    {/*                                     props.buttonEmoji !== undefined && props.buttonEmoji !== null */}
                    {/*                                         ? getImageUrlFromGeneralImage(buttonEmojiObj) */}
                    {/*                                         : "No image provided" */}
                    {/*                                 } */}
                    {/*                                 alt={ */}
                    {/*                                     props.buttonEmojiAlt !== undefined && */}
                    {/*                                     props.buttonEmojiAlt !== null */}
                    {/*                                         ? getAltTextFromGeneralImage(buttonEmojiObj) */}
                    {/*                                         : "No image provided" */}
                    {/*                                 } */}
                    {/*                                 layout="fill" */}
                    {/*                             /> */}
                    {/*                         </div> */}
                    {/*                     )} */}
                    {/*                 </div> */}
                    {/*             </button> */}
                    {/*         </div> */}
                    {/*     </div> */}
                    {/* </div> */}
                    <div
                        className={clsx("relative w-full overflow-x-clip animatedHeight")}
                        style={
                            isOnline
                                ? {}
                                : {
                                    height: height + "px",
                                }
                        }
                    >
                        <div
                            className={clsx(
                                "delay-300",
                                // !animationFinished ? "translate-x-[110%] opacity-0" : "chatEnter"
                            )}
                        >
                            <div
                                className={clsx(
                                    "transition-all duration-[5000ms] delay-0",
                                    // "group-hover:opacity-100",
                                    //props.isMainChatMessage ? " chatButtonFadeIn pointer-events-auto " : " chatButtonFadeOut pointer-events-none "
                                )}
                                style={
                                    isOnline
                                        ? {}
                                        : {
                                            height: height + "px",
                                        }
                                }
                            >
                                <div className={clsx("flex flex-row justify-center gap-8 shadow-scroll rounded-xxxs")}>
                                    <div className={clsx("text-white", isOnline ? "" : "my-8 ml-8")}>
                                        <div
                                            className="w-full font-black text-[48px] text-bold-dynamic-petrol uppercase tracking-[1px]">
                                            {props.title}
                                        </div>
                                        <div
                                            className={clsx(
                                                "mt-2 w-full leading-[22px]",
                                                isOnline ? "text-[12px]" : "text-[52px]",
                                            )}
                                        >
                                            <ReactMarkdown>{props.text}</ReactMarkdown>
                                        </div>
                                        <div
                                            className={clsx(
                                                "flex flex-wrap justify-center mt-[16px] mb-[16px]",
                                                isOnline ? "gap-[9px]" : "gap-[32px]",
                                            )}
                                        >
                                            <ChatButton
                                                id={buttonId}
                                                disabled={buttonPressed}
                                                onClick={() => {
                                                    /*if (props.buttonText === "Ja, klar!") {
                                                    updateRenderContent(UiState.END_ONE)
                                                }*/
                                                    //if(!isNaN(buttonId)) updateButtonById(buttonId, true);
                                                    updateChatTimer(false);
                                                    addBranchToThread(props.buttons[0]);
                                                }}
                                                emoji={props.buttonEmoji}
                                                emojiAlt={props.buttonEmojiAlt}
                                            >{props.buttonText}</ChatButton>
                                            {props.buttonTextTwo !== "" ?
                                                <ChatButton
                                                    id={buttonIdTwo}
                                                    disabled={buttonPressed}
                                                    onClick={() => {
                                                        /*if (props.buttonText === "Ja, klar!") {
                                                        updateRenderContent(UiState.END_ONE)
                                                    }*/
                                                        //if(!isNaN(buttonIdTwo)) updateButtonById(buttonIdTwo, true);
                                                        updateChatTimer(false);
                                                        addBranchToThread(props.buttons[1]);
                                                    }}
                                                    emoji={props.buttonTwoEmoji}
                                                    emojiAlt={props.buttonThreeEmojiAlt}
                                                >{props.buttonTextTwo}</ChatButton> : null}
                                            {props.buttonTextThree !== "" ?
                                                <ChatButton
                                                    id={buttonIdThree}
                                                    disabled={buttonPressed}
                                                    onClick={() => {
                                                        /*if (props.buttonText === "Ja, klar!") {
                                                        updateRenderContent(UiState.END_ONE)
                                                    }*/
                                                        //if(!isNaN(buttonIdThree)) updateButtonById(buttonIdThree, true);
                                                        updateChatTimer(false);
                                                        addBranchToThread(props.buttons[2]);
                                                    }}
                                                    emoji={props.buttonThreeEmoji}
                                                    emojiAlt={props.buttonThreeEmojiAlt}
                                                >{props.buttonTextThree}</ChatButton> : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default inject("store")(observer(ChatMessageButton));
